import * as THREE from 'three';

import { type RacerState } from './Physics';
import {
  getTrackAIPath,
  sampleTrackAIPath,
  type TrackAIPath,
  type TrackAIPathOptions,
  type TrackAIPathSample,
} from './TrackAIPath';
import { locateTrackPosition, type Track } from '../track/TrackLoader';

export interface OpponentProfile {
  id: string;
  speedMultiplier: number;
  acceleration: number;
  braking: number;
  laneOffset: number;
  racingLineStrength: number;
}

export interface OpponentTelemetryLap {
  lapTimeMs: number;
  maxAbsLateral: number;
  minTrackMargin: number;
}

export interface OpponentControllerState {
  profile: OpponentProfile;
  path: TrackAIPath;
  progress: number;
  speed: number;
  lapsCompleted: number;
  steps: number;
  lapStartedAtMs: number;
  lastSample: TrackAIPathSample;
  lastTrackMargin: number;
  maxAbsLateralThisLap: number;
  minTrackMarginThisLap: number;
  laps: OpponentTelemetryLap[];
}

export interface OpponentStepContext {
  nowMs?: number;
  groundY?: number;
}

export const BALANCED_OPPONENT_PROFILE: OpponentProfile = {
  id: 'balanced',
  speedMultiplier: 1,
  acceleration: 48,
  braking: 70,
  laneOffset: -0.8,
  racingLineStrength: 0.34,
};

export const AGGRESSIVE_OPPONENT_PROFILE: OpponentProfile = {
  id: 'aggressive',
  speedMultiplier: 1.05,
  acceleration: 54,
  braking: 76,
  laneOffset: 0.7,
  racingLineStrength: 0.42,
};

export const TIMID_OPPONENT_PROFILE: OpponentProfile = {
  id: 'timid',
  speedMultiplier: 0.82,
  acceleration: 36,
  braking: 54,
  laneOffset: 1.1,
  racingLineStrength: 0.2,
};

export function createOpponentControllerState(
  track: Track,
  profile: Partial<OpponentProfile> = {},
  pathOptions: TrackAIPathOptions = {},
): OpponentControllerState {
  const mergedProfile = { ...BALANCED_OPPONENT_PROFILE, ...profile };
  const path = getTrackAIPath(track, {
    ...pathOptions,
    laneOffset: mergedProfile.laneOffset,
    racingLineStrength: mergedProfile.racingLineStrength,
  });
  const sample = sampleTrackAIPath(path, 0);
  return {
    profile: mergedProfile,
    path,
    progress: 0,
    speed: 0,
    lapsCompleted: 0,
    steps: 0,
    lapStartedAtMs: 0,
    lastSample: sample,
    lastTrackMargin: trackMarginAt(track, sample.x, sample.z),
    maxAbsLateralThisLap: Math.abs(locateTrackPosition(track, sample.x, sample.z).signedLateral),
    minTrackMarginThisLap: trackMarginAt(track, sample.x, sample.z),
    laps: [],
  };
}

export function resetOpponentController(
  track: Track,
  controller: OpponentControllerState,
  nowMs = 0,
): void {
  controller.progress = 0;
  controller.speed = 0;
  controller.lapsCompleted = 0;
  controller.steps = 0;
  controller.lapStartedAtMs = nowMs;
  controller.lastSample = sampleTrackAIPath(controller.path, 0);
  controller.lastTrackMargin = trackMarginAt(track, controller.lastSample.x, controller.lastSample.z);
  controller.maxAbsLateralThisLap = Math.abs(locateTrackPosition(track, controller.lastSample.x, controller.lastSample.z).signedLateral);
  controller.minTrackMarginThisLap = controller.lastTrackMargin;
  controller.laps = [];
}

export function placeOpponentAtStart(
  track: Track,
  controller: OpponentControllerState,
  state: RacerState,
  groundY = 0,
): void {
  const sample = sampleTrackAIPath(controller.path, controller.progress);
  writeRacerStateFromSample(state, sample, controller.speed, groundY);
  const margin = trackMarginAt(track, sample.x, sample.z);
  controller.lastSample = sample;
  controller.lastTrackMargin = margin;
}

export function stepOpponentController(
  track: Track,
  controller: OpponentControllerState,
  racerState: RacerState,
  dt: number,
  context: OpponentStepContext = {},
): void {
  const safeDt = Math.max(0, Math.min(dt, 1));
  controller.steps += 1;
  const sample = sampleTrackAIPath(controller.path, controller.progress);
  const targetSpeed = sample.targetSpeed * controller.profile.speedMultiplier;
  const maxDelta = (targetSpeed >= controller.speed ? controller.profile.acceleration : controller.profile.braking) * safeDt;
  controller.speed = approach(controller.speed, targetSpeed, maxDelta);

  const previousProgress = controller.progress;
  controller.progress += controller.speed * safeDt;
  if (controller.progress >= controller.path.totalLength) {
    const completed = Math.floor(controller.progress / controller.path.totalLength);
    controller.progress %= controller.path.totalLength;
    controller.lapsCompleted += completed;
    closeOpponentLap(track, controller, context.nowMs ?? 0);
  }

  const nextSample = sampleTrackAIPath(controller.path, controller.progress);
  writeRacerStateFromSample(racerState, nextSample, controller.speed, context.groundY ?? racerState.position.y);
  const pos = locateTrackPosition(track, racerState.position.x, racerState.position.z);
  const margin = trackLimitMargin(pos.signedLateral, pos.widthLeft, pos.widthRight);
  controller.lastSample = nextSample;
  controller.lastTrackMargin = margin;
  controller.maxAbsLateralThisLap = Math.max(controller.maxAbsLateralThisLap, Math.abs(pos.signedLateral));
  controller.minTrackMarginThisLap = Math.min(controller.minTrackMarginThisLap, margin);

  if (controller.progress < previousProgress && controller.lapsCompleted === 0) {
    controller.lapsCompleted = 1;
  }
}

export function opponentDiagnostics(controller: OpponentControllerState): {
  id: string;
  progress: number;
  speed: number;
  laps: number;
  steps: number;
  lastTrackMargin: number;
  maxAbsLateralThisLap: number;
  minTrackMarginThisLap: number;
  lastLapMs: number | null;
  targetSpeed: number;
  lapHistory: OpponentTelemetryLap[];
} {
  const sample = sampleTrackAIPath(controller.path, controller.progress);
  return {
    id: controller.profile.id,
    progress: controller.progress,
    speed: controller.speed,
    laps: controller.lapsCompleted,
    steps: controller.steps,
    lastTrackMargin: controller.lastTrackMargin,
    maxAbsLateralThisLap: controller.maxAbsLateralThisLap,
    minTrackMarginThisLap: controller.minTrackMarginThisLap,
    lastLapMs: controller.laps.at(-1)?.lapTimeMs ?? null,
    targetSpeed: sample.targetSpeed * controller.profile.speedMultiplier,
    lapHistory: controller.laps.slice(),
  };
}

function closeOpponentLap(track: Track, controller: OpponentControllerState, nowMs: number): void {
  if (controller.lapStartedAtMs >= 0 && nowMs >= controller.lapStartedAtMs) {
    controller.laps.push({
      lapTimeMs: nowMs - controller.lapStartedAtMs,
      maxAbsLateral: controller.maxAbsLateralThisLap,
      minTrackMargin: controller.minTrackMarginThisLap,
    });
    while (controller.laps.length > 10) controller.laps.shift();
  }
  controller.lapStartedAtMs = nowMs;
  const sample = sampleTrackAIPath(controller.path, controller.progress);
  const pos = locateTrackPosition(track, sample.x, sample.z);
  controller.maxAbsLateralThisLap = Math.abs(pos.signedLateral);
  controller.minTrackMarginThisLap = trackLimitMargin(pos.signedLateral, pos.widthLeft, pos.widthRight);
}

function writeRacerStateFromSample(state: RacerState, sample: TrackAIPathSample, speed: number, groundY: number): void {
  state.position.set(sample.x, groundY, sample.z);
  state.velocity.set(sample.tx * speed, 0, sample.tz * speed);
  state.heading = Math.atan2(sample.tx, sample.tz);
  state.yawVelocity = 0;
  state.verticalVelocity = 0;
  state.airborne = false;
}

function trackMarginAt(track: Track, x: number, z: number): number {
  const pos = locateTrackPosition(track, x, z);
  return trackLimitMargin(pos.signedLateral, pos.widthLeft, pos.widthRight);
}

function trackLimitMargin(signedLateral: number, widthLeft: number, widthRight: number): number {
  return signedLateral >= 0
    ? widthLeft - signedLateral
    : widthRight + signedLateral;
}

function approach(value: number, target: number, maxDelta: number): number {
  if (value < target) return Math.min(target, value + maxDelta);
  return Math.max(target, value - maxDelta);
}

export function cloneRacerState(state: RacerState): RacerState {
  return {
    position: state.position.clone(),
    velocity: state.velocity.clone(),
    heading: state.heading,
    yawVelocity: state.yawVelocity,
    driftCharge: state.driftCharge,
    boostTimer: state.boostTimer,
    isDrifting: state.isDrifting,
    verticalVelocity: state.verticalVelocity,
    airborne: state.airborne,
  };
}

export function makeOpponentRacerState(): RacerState {
  return {
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    heading: 0,
    yawVelocity: 0,
    driftCharge: 0,
    boostTimer: 0,
    isDrifting: false,
    verticalVelocity: 0,
    airborne: false,
  };
}
